import React, { useState, useEffect } from 'react';
import '../FishFinderBuilder.css';
import ConfirmationModal from './ConfirmationModal';
import { submitFeedback } from '../utils/api';
import { screensData } from '../data/productData';
import ImageModal from './ImageModal';
import { getDealLinkById } from '../data/dealLinks';

const ScreenSelection = ({
  screenAttrs = {},            // default to an empty object
  setScreenAttrs = () => {},
  selectedScreen = null,
  setSelectedScreen = () => {},
  nextStep = () => {},
  resetAll = () => {},
  jumpToStep = () => {},
  isQuickCompare = false,
  isInModal = false,
  onNavigateToFullPage = () => {}
}) => {
  const [dropdownValue, setDropdownValue] = useState("");
  const [showHelp, setShowHelp] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    selections: [],
    pendingChange: null
  });
  const [feedbackStatus, setFeedbackStatus] = useState({
    type: null,
    message: null
  });

  // Add state for the image modal
  const [modalImage, setModalImage] = useState({
    isOpen: false,
    url: '',
    alt: ''
  });

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize screenAttrs with default values
  useEffect(() => {
    if (!Object.keys(screenAttrs).length) {
      setScreenAttrs({
        screenSize: "",
        viewOnPhone: "",
        hdmiOut: "",
        hdmiIn: "",
        networkPorts: "",
        bncVideoIn: "",
        controlMethod: ""
      });
    }
  }, []);

  const handleSelectDropdown = (event) => {
    const selectedId = event.target.value;
    setDropdownValue(selectedId);
    
    if (selectedId === "") {
      // If "Select a screen" is chosen, reset
      setSelectedScreen(null);
      setScreenAttrs({
        screenSize: "",
        viewOnPhone: "",
        hdmiOut: "",
        hdmiIn: "",
        networkPorts: "",
        bncVideoIn: "",
        controlMethod: ""
      });
      return;
    }
    
    // Find the selected screen by ID
    const screen = screensData.find(s => s.id === selectedId);
    
    if (screen) {
      // Set the selected screen
      setSelectedScreen(screen);
      
      // Also set the screen attributes to match the selected screen
      setScreenAttrs({
        screenSize: screen.screenSize || "",
        viewOnPhone: screen.viewOnPhone || "",
        hdmiOut: screen.hdmiOut || "",
        hdmiIn: screen.hdmiIn || "",
        networkPorts: screen.networkPorts || "",
        bncVideoIn: screen.bncVideoIn || "",
        controlMethod: screen.controlMethod || ""
      });
    }
  };

  const handleResetOptions = () => {
    setSelectedScreen(null);
    setScreenAttrs({
      screenSize: "",
      viewOnPhone: "",
      hdmiOut: "",
      hdmiIn: "",
      networkPorts: "",
      bncVideoIn: "",
      controlMethod: ""
    });
    setDropdownValue("");
  };

  // 1. First, add this function to create a deep copy of your data
  const deepCopy = (obj) => JSON.parse(JSON.stringify(obj));

  // 2. When getting matching screens, work with a copy of the data
  const getMatchingScreens = (attrs) => {
    // Create a deep copy of screensData to avoid modifying the original
    const screensCopy = deepCopy(screensData);
    
    return screensCopy.filter((screen) => {
      if (attrs.screenSize && screen.screenSize !== attrs.screenSize) return false;
      if (attrs.viewOnPhone === "Yes" && screen.viewOnPhone !== "Yes") return false;
      if (attrs.hdmiOut === "Yes" && screen.hdmiOut !== "Yes") return false;
      if (attrs.hdmiIn === "Yes" && screen.hdmiIn !== "Yes") return false;
      
      // Updated network ports filtering logic
      if (attrs.networkPorts === "At least 2" && parseInt(screen.networkPorts) < 2) return false;
      if (attrs.networkPorts === "At least 1" && parseInt(screen.networkPorts) < 1) return false;
      
      if (attrs.bncVideoIn === "Yes" && screen.bncVideoIn !== "Yes") return false;
      if (attrs.controlMethod === "Touchscreen" && screen.controlMethod !== "Touchscreen") return false;
      if (attrs.controlMethod === "Keypad" && screen.controlMethod !== "Keypad") return false;
      return true;
    });
  };

  const isOptionValid = (attrName, opt) => {
    if (attrName !== "screenSize" && attrName !== "controlMethod" && 
        attrName !== "networkPorts" && opt === "Not Necessary")
      return true;
    if (attrName === "controlMethod" && opt === "No preference") return true;
    if (attrName === "networkPorts" && opt === "No preference") return true;
    if (attrName === "networkPorts" && opt === "At least 1") return true; // All models have at least 1
    const temp = { ...screenAttrs, [attrName]: opt };
    return getMatchingScreens(temp).length > 0;
  };

  const getReason = (attrName, opt) => {
    if (opt === "Not Necessary" || 
        (attrName === "controlMethod" && opt === "No preference") ||
        (attrName === "networkPorts" && opt === "No preference"))
      return "";
    if (attrName === "screenSize")
      return `No models support that combination with ${opt}″.`;
    if (attrName === "viewOnPhone")
      return "To enable phone viewing, choose a larger screen or a GPSMAP model.";
    if (attrName === "hdmiOut")
      return "Requires certain 10″ or 12″ GPSMAP models.";
    if (attrName === "hdmiIn")
      return "Only on 10″ or 12″ GPSMAP models with HDMI Out.";
    if (attrName === "networkPorts") {
      if (opt === "At least 2")
        return "Only available on ECHOMAP Ultra 2 and certain GPSMAP models.";
      return "All models have at least 1 network port.";
    }
    if (attrName === "bncVideoIn")
      return "Only on specific GPSMAP models (e.g., 943xsv, 8610xsv).";
    if (attrName === "controlMethod") {
      if (opt === "Touchscreen")
        return "No touchscreen models support these selections.";
      if (opt === "Keypad")
        return "No keypad models support these selections.";
    }
    return "Not available with current selections.";
  };

  const handleChange = (attrName, value, valid) => {
    // If clicking the same option that's already selected, clear it
    if (screenAttrs[attrName] === value) {
      const newAttrs = { ...screenAttrs, [attrName]: null };
      setScreenAttrs(newAttrs);
      setSelectedScreen(null);
      setDropdownValue("");
      return;
    }

    // Otherwise, proceed with existing logic for selecting a new option
    if (valid) {
      const newAttrs = { ...screenAttrs, [attrName]: value };
      setScreenAttrs(newAttrs);
      setSelectedScreen(null);
      setDropdownValue("");
      return;
    }

    // For invalid options, find what needs to be cleared
    const currentSelections = Object.entries(screenAttrs)
      .filter(([key, val]) => val && val !== "Not Necessary" && key !== attrName)
      .map(([key, val]) => {
        const label = attributes.find(attr => attr.name === key)?.label || key;
        return `${label}: ${val}`;
      });

    if (currentSelections.length > 0) {
      const featureLabel = attributes.find(attr => attr.name === attrName)?.label || attrName;
      const valueLabel = attrName === "screenSize" ? `${value}″` : value;
      
      setModalConfig({
        isOpen: true,
        title: "Conflicting Selections",
        message: `To select "${valueLabel}" for "${featureLabel}", the following choices need to be cleared:`,
        selections: currentSelections,
        pendingChange: { attrName, value }
      });  
    }
  };

  const handleModalConfirm = () => {
    const { attrName, value } = modalConfig.pendingChange;
    const newAttrs = { ...screenAttrs };
    
    // Clear conflicting selections
    Object.keys(newAttrs).forEach(key => {
      if (key !== attrName) {
        newAttrs[key] = '';
      }
    });
    
    // Set new selection
    newAttrs[attrName] = value;
    setScreenAttrs(newAttrs);
    setSelectedScreen(null);
    setDropdownValue("");
    setModalConfig({ isOpen: false, selections: [], pendingChange: null });
  };

  const handleModalClose = () => {
    setModalConfig({ isOpen: false, selections: [], pendingChange: null });
  };

  const attributes = [
    { label: "Screen Size", name: "screenSize", options: ["7", "9", "10", "12"] },
    { label: "View on Phone", name: "viewOnPhone", options: ["Yes", "Not Necessary"] },
    { label: "HDMI Out", name: "hdmiOut", options: ["Yes", "Not Necessary"] },
    { label: "HDMI In", name: "hdmiIn", options: ["Yes", "Not Necessary"] },
    { label: "# of LiveScope Ports", name: "networkPorts", options: ["At least 2", "At least 1", "No preference"] },
    { label: "BNC Video In", name: "bncVideoIn", options: ["Yes", "Not Necessary"] },
    { label: "Control Method", name: "controlMethod", options: ["Touchscreen", "Keypad", "No preference"] }
  ];

  const matchingScreens = getMatchingScreens(screenAttrs);

  console.log("Data used for rendering:", getMatchingScreens(screenAttrs));

  const featureKeys = [
    { key: 'screenSize', label: 'Screen Size', suffix: '″' },
    { key: 'viewOnPhone', label: 'View on Phone' },
    { key: 'hdmiOut', label: 'HDMI Out' },
    { key: 'hdmiIn', label: 'HDMI In' },
    { key: 'networkPorts', label: '# of Network (LiveScope) Ports' },
    { key: 'bncVideoIn', label: 'BNC Video In' },
    { key: 'controlMethod', label: 'Control' }
  ];

  const hasAnySelection = () => {
    return Object.values(screenAttrs).some(value => value !== "");
  };

  const handleSubmitFeedback = async (comment) => {
    try {
      await submitFeedback(comment);
      setFeedbackStatus({
        type: 'success',
        message: 'Thank you for sharing your experience! Alex just got it emailed to him and reviews every one of them!'
      });
      // ... rest of the handler
    } catch (error) {
      // ... error handling
    }
  };

  // Function to format screen size with inches symbol
  const formatScreenSize = (size) => {
    if (!size) return '';
    
    // If size already includes the inches symbol, return as is
    if (size.includes('″') || size.includes('"') || size.includes('in')) {
      return size;
    }
    
    // Otherwise, add the inches symbol
    return `${size}″`;
  };

  // Define the clickable card style
  const clickableCardStyle = {
    position: 'relative',
    cursor: 'pointer',
    zIndex: 10
  };

  // Clean up the handleCompatibleScreenClick function
  const handleCompatibleScreenClick = (screen) => {
    console.log("Screen clicked:", screen.name);
    if (selectedScreen && selectedScreen.name === screen.name) {
      // If clicking the already selected screen, deselect it
      setSelectedScreen(null);
    } else {
      // Otherwise, select the clicked screen
      setSelectedScreen(screen);
    }
  };

  // Update dropdown value when selectedScreen changes (for consistency)
  useEffect(() => {
    if (selectedScreen) {
      setDropdownValue(selectedScreen.id);
    } else {
      setDropdownValue("");
    }
  }, [selectedScreen]);

  // Only add these styles for the green outline treatment
  const getCardStyle = (isSelected) => {
    if (isSelected) {
      return {
        border: '2px solid #4CAF50',
        boxShadow: '0 0 0 2px #4CAF50',
        opacity: 1
      };
    }
    return {
      border: '2px solid #444',
      opacity: 0.85
    };
  };

  // 3. Update your handleImageError function to only modify the DOM element
  const handleImageError = (e, screenName) => {
    console.error(`Image failed to load for ${screenName}: ${e.target.src}`);
    
    // Only change the src attribute of the img element, not the original data
    e.target.src = `https://dummyimage.com/150x100/cccccc/000000&text=${screenName.replace(/\s+/g, '+')}`;
    e.target.onerror = null; // Prevent infinite error loop
  };

  // Function to open the modal with a specific image
  const openImageModal = (imageUrl, altText) => {
    setModalImage({
      isOpen: true,
      url: imageUrl,
      alt: altText
    });
  };
  
  // Function to close the modal
  const closeImageModal = () => {
    setModalImage({
      isOpen: false,
      url: '',
      alt: ''
    });
  };

  // Add the MagnifyIcon component (same as in TransducerSelection)
  const MagnifyIcon = () => (
    <div className="magnify-icon">
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
      </svg>
    </div>
  );

  // Update the image container in the ScreenOption component
  const ScreenOption = ({ screen, allVisibleScreens }) => {
    const isSelected = selectedScreen && selectedScreen.name === screen.name;
    
    // Create a local copy of the image path
    const [imageSrc, setImageSrc] = useState(screen.image);
    
    // Add the getSpecClassName function inside ScreenOption
    const getSpecClassName = (specName, specValue) => {
      const values = allVisibleScreens.map(s => s[specName]);
      return values.some(v => v !== specValue) ? 'feature-row different' : 'feature-row';
    };

    // Update the formatPrice function to NOT include the currency symbol
    const formatPrice = (price) => {
      return price.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    };

    // Use getDealLinkById from the imported functions
    const getDealLink = () => {
      if (!screen) return '';
      return getDealLinkById('screen', screen.id) || screen.productLink || '';
    };

    return (
      <div
        className={`option-row ${isSelected ? "selected" : ""}`}
        onClick={() => handleCompatibleScreenClick(screen)}
      >
        <div className="screen-left-section">
          <div className="screen-image-container">
            <img 
              src={screen.image}
              alt={screen.name}
              onClick={(e) => {
                e.stopPropagation();
                openImageModal(screen.image, screen.name);
              }}
              onError={(e) => {
                setImageSrc(`https://dummyimage.com/150x100/cccccc/000000&text=${screen.name.replace(/\s+/g, '+')}`);
                e.target.onerror = null;
              }}
            />
            <MagnifyIcon />
          </div>
          <div className="screen-card-content">
            <h3 className="screen-title">{screen.name}</h3>
            <p className="msrp">
              MSRP: <span className="price-value">${formatPrice(screen.msrp)}</span>
            </p>
            <div className="action-buttons">
              <button 
                className={`select-button ${isSelected ? 'selected' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCompatibleScreenClick(screen);
                  if (isSelected) {
                    nextStep();
                  }
                }}
              >
                {isSelected ? 'Continue with Build ✓' : 'Select for Build'}
              </button>
              {getDealLink() && (
                <a 
                  href={getDealLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className="best-price-button"
                >
                  Or Find the Best Price Now
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="screen-right-section">
          <div className="features-list">
            <div className={getSpecClassName('resolution', screen.resolution)}>
              <div className="feature-label">Resolution:</div>
              <div className="feature-value">{screen.resolution}</div>
            </div>
            <div className={getSpecClassName('screenSize', screen.screenSize)}>
              <div className="feature-label">Screen Size:</div>
              <div className="feature-value">{formatScreenSize(screen.screenSize)}</div>
            </div>
            <div className={getSpecClassName('viewOnPhone', screen.viewOnPhone)}>
              <div className="feature-label">View on Phone:</div>
              <div className="feature-value">{screen.viewOnPhone}</div>
            </div>
            <div className={getSpecClassName('hdmiOut', screen.hdmiOut)}>
              <div className="feature-label">HDMI Out:</div>
              <div className="feature-value">{screen.hdmiOut}</div>
            </div>
            <div className={getSpecClassName('hdmiIn', screen.hdmiIn)}>
              <div className="feature-label">HDMI In:</div>
              <div className="feature-value">{screen.hdmiIn}</div>
            </div>
            <div className={getSpecClassName('networkPorts', screen.networkPorts)}>
              <div className="feature-label"># of LiveScope Ports:</div>
              <div className="feature-value">{screen.networkPorts}</div>
            </div>
            <div className={getSpecClassName('bncVideoIn', screen.bncVideoIn)}>
              <div className="feature-label">BNC Video In:</div>
              <div className="feature-value">{screen.bncVideoIn}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Add this to your component to log when it renders
  console.log("ScreenSelection rendering");
  console.log("Screen options:", screensData);

  // Add this useEffect to your ScreenSelection component to debug image loading
  useEffect(() => {
    // Log the image paths for the Echomap 93sv UHD2
    const echomap93 = screensData.find(screen => screen.name === "Echomap 93sv UHD2");
    if (echomap93) {
      console.log("Echomap 93sv UHD2 image path:", echomap93.image);
      
      // Try to fetch the image to see if it exists
      fetch(echomap93.image)
        .then(response => {
          console.log("Image fetch response:", response);
          if (!response.ok) {
            console.error("Image not found:", response.status);
          }
        })
        .catch(error => {
          console.error("Error fetching image:", error);
        });
    }
  }, []);

  const renderCompatibleScreens = () => {
    const matchingScreens = getMatchingScreens(screenAttrs);
    
    if (matchingScreens.length === 0) {
      return (
        <div className="no-screens-message">
          No compatible screens found. Please adjust your selections.
        </div>
      );
    }

    return (
      <div className="compatible-screens">
        {matchingScreens.length > 1 && (
          <>
            <div className="multiple-options-indicator">
              Multiple compatible screens found. Click on each option to compare features.
            </div>
            <div className="multiple-options-helper">
              Tip: Look for the blue highlighted text to see key differences between models
            </div>
          </>
        )}
        <div style={{
          display: 'grid',
          gridTemplateColumns: windowWidth >= 1200 ? '1fr 1fr' : '1fr',
          gap: '20px',
          marginTop: '20px',
          width: '100%'
        }}>
          {matchingScreens.map((screen) => (
            <ScreenOption 
              key={screen.id}
              screen={screen} 
              allVisibleScreens={matchingScreens}
            />
          ))}
        </div>
      </div>
    );
  };

  // Add a direct option clicked handler for debugging
  const handleOptionClicked = (e, attrName, value, isValid) => {
    console.log(`Direct click handler called for ${value} on attribute ${attrName}. Valid: ${isValid}`);
    
    // If valid option
    if (isValid) {
      // If already selected, unselect it
      if (screenAttrs[attrName] === value) {
        const newAttrs = { ...screenAttrs };
        newAttrs[attrName] = "";
        setScreenAttrs(newAttrs);
        setSelectedScreen(null);
        setDropdownValue("");
        console.log(`Deselected ${value} for ${attrName}`);
      } else {
        // Otherwise, select it
        const newAttrs = { ...screenAttrs };
        newAttrs[attrName] = value;
        setScreenAttrs(newAttrs);
        setSelectedScreen(null);
        setDropdownValue("");
        console.log(`Selected ${value} for ${attrName}`);
      }
    } else {
      // For invalid options, show modal
      const currentSelections = Object.entries(screenAttrs)
        .filter(([key, val]) => val && val !== "Not Necessary" && key !== attrName)
        .map(([key, val]) => {
          const label = attributes.find(attr => attr.name === key)?.label || key;
          return `${label}: ${val}`;
        });

      if (currentSelections.length > 0) {
        const featureLabel = attributes.find(a => a.name === attrName)?.label || attrName;
        const valueLabel = attrName === "screenSize" ? `${value}″` : value;
        
        setModalConfig({
          isOpen: true,
          title: "Conflicting Selections",
          message: `To select "${valueLabel}" for "${featureLabel}", the following choices need to be cleared:`,
          selections: currentSelections,
          pendingChange: { attrName, value }
        });
        console.log(`Showed conflict modal for ${value}`);
      }
    }
  };

  return (
    <div className="screen-selection-container">
      {isInModal && (
        <div className="redirect-container" style={{
          marginBottom: '20px',
          textAlign: 'center',
          padding: '15px',
          backgroundColor: '#0066cc',
          borderRadius: '6px'
        }}>
          <button 
            onClick={onNavigateToFullPage}
            style={{
              fontSize: '16px',
              padding: '10px 20px',
              backgroundColor: '#fff',
              color: '#0066cc',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
              width: '100%'
            }}
          >
            Open Full Screen Selection Page
          </button>
          <p style={{
            color: 'white',
            marginTop: '10px',
            fontSize: '14px'
          }}>
            For better experience, use the full selection page
          </p>
        </div>
      )}

      <h1 style={{ color: 'white', marginBottom: '10px' }}>Screen</h1>
      <p style={{ color: 'white', marginBottom: '30px' }}>You can choose your screen in two ways:</p>

      <div className="selection-methods">
        {/* Quick Select Option */}
        <div className="quick-select-container">
          <h3 className="method-label">Option 1: Quick Select</h3>
          <select 
            value={selectedScreen ? selectedScreen.id : ''} 
            onChange={handleSelectDropdown}
            className="screen-dropdown"
          >
            <option value="">Select a screen</option>
            {screensData.map(screen => (
              <option key={screen.id} value={screen.id}>
                {screen.name} - {screen.screenSize}″
              </option>
            ))}
          </select>
          {selectedScreen && (
            <button 
              onClick={nextStep}
              className="nav-btn glowing-button"
              style={{ 
                marginTop: '15px',
                width: '100%',
                padding: '10px',
                fontSize: '16px'
              }}
            >
              Continue with Build ✓
            </button>
          )}
        </div>

        {/* Find by Features Option */}
        <div className="guided-selection" onClick={(e) => e.stopPropagation()}>
          <h3 className="method-label">Option 2: Find by Features</h3>
          <p className="method-description">
            Or answer the questions below to see compatible screens based on your preferences
          </p>
          
          {/* Questions section moved inside guided-selection */}
          <div className="options-container">
            {attributes.map((attr) => (
              <div key={attr.name} className="question-block" onClick={(e) => e.stopPropagation()}>
                <h3 className="question-title">{attr.label}</h3>
                <div className="options-container">
                  {attr.options.map((opt) => {
                    const valid = isOptionValid(attr.name, opt);
                    const reason = getReason(attr.name, opt);
                    const checked = screenAttrs[attr.name] === opt;
                    
                    // Format the label for screen size options
                    const labelText = attr.name === "screenSize" 
                      ? `${opt}″`  // Add back the inch symbol
                      : opt;

                    return (
                      <div 
                        key={opt}
                        className={`option-row ${checked ? "selected" : ""}`}
                        data-attribute={attr.name}
                        data-value={opt}
                        data-valid={valid.toString()}
                        style={{ 
                          cursor: 'pointer', 
                          position: 'relative', 
                          zIndex: 3000
                        }}
                        onClick={(e) => {
                          // Handle clicks on the row, but not on the radio input itself
                          if (e.target.type !== 'radio') {
                            e.preventDefault();
                            e.stopPropagation();
                            handleOptionClicked(e, attr.name, opt, valid);
                          }
                        }}
                      >
                        <input
                          type="radio"
                          name={attr.name}
                          id={`${attr.name}-${opt}`}
                          value={opt}
                          checked={checked}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleOptionClicked(e, attr.name, opt, valid);
                          }}
                          onClick={(e) => {
                            // Prevent click from bubbling up to the row
                            e.stopPropagation();
                          }}
                        />
                        <label 
                          htmlFor={`${attr.name}-${opt}`}
                          className="option-label"
                          onClick={(e) => {
                            // Let the row's onClick handler process this
                            e.preventDefault();
                          }}
                        >
                          {labelText}
                          {!valid && reason && <span className="reason">— {reason}</span>}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {renderCompatibleScreens()}
      
      {/* Navigation buttons */}
      <div className="navigation-buttons">
        {isInModal || isQuickCompare ? (
          <button 
            onClick={nextStep} 
            className={`nav-btn ${selectedScreen ? "glowing-button" : ""}`}
            disabled={!selectedScreen}
          >
            {isQuickCompare ? 'View Details' : 'Next'}
          </button>
        ) : (
          <>
            <button 
              onClick={nextStep} 
              className="nav-btn back-btn"
            >
              Back to Builder
            </button>
            {selectedScreen && (
              <button 
                onClick={nextStep} 
                className="nav-btn glowing-button"
              >
                Continue with Selection
              </button>
            )}
            <button onClick={resetAll} className="nav-btn reset-btn">
              Reset Selections
            </button>
          </>
        )}
      </div>

      {/* Step indicator / Breadcrumbs - only show in original builder flow */}
      {!isQuickCompare && isInModal && (
        <div className="step-indicator">
          <div className="step-progress">
            <div className="step-label">Jump to:</div>
            <button onClick={() => jumpToStep(1)} className="step-btn active">1. Screen</button>
            <button onClick={() => jumpToStep(2)} className="step-btn">2. Transducer</button>
            <button onClick={() => jumpToStep(3)} className="step-btn">3. Battery</button>
            <button onClick={() => jumpToStep(4)} className="step-btn">4. Shuttle</button>
            <button onClick={() => jumpToStep(5)} className="step-btn">5. Pole</button>
            <button onClick={() => jumpToStep(6)} className="step-btn summary-link">Bundle Summary</button>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={modalConfig.isOpen}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        selections={modalConfig.selections}
      />

      <ImageModal 
        isOpen={modalImage.isOpen}
        imageUrl={modalImage.url}
        altText={modalImage.alt}
        onClose={closeImageModal}
      />
    </div>
  );
};

export default ScreenSelection;
